"use client";

import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import axios from "axios";
import FormInput from "@/components/FormInput";
import FormSelect from "@/components/FormSelect";
import FormCheckbox from "@/components/FormCheckbox";
import DateInput from "@/components/DateInput";
import IntlPhoneInput, { type IntlPhoneInputHandle } from "@/components/IntlPhoneInput";
import CountryTimezoneSelect from "./CountryTimezoneSelect";
import { completeGoogleProfileApi } from "@/modules/auth/services/auth.api";
import { useRegisterMetadata } from "@/modules/auth/hooks/useRegisterMetadata";
import { ENGLISH_NAME_REGEX, withEnglishNameFilter } from "@/lib/name";
import { formatDateOnly } from "@/lib/date";
import { buildCountryTimezoneGroups, countryForTimezone } from "@/services/countryTimezone";
import { getRegionLocaleHref } from "@/services/region";
import { canonicalTimezone } from "@/services/usTimezones";

type Props = {
  /**
   * Exactly what the backend says is still missing, in RegisterRequest's own
   * vocabulary ("country", not "country_code"). Rendering off this rather than
   * a hard-coded list means a new required field propagates here on its own.
   */
  missingFields: string[];
  onCompleted: () => void;
  children: (rendered: RenderedProfileFields) => React.ReactNode;
};

/** The controls this component owns, for the wizard to place. */
export type RenderedProfileFields = {
  /** The save-failed banner, or null. Rendered by the wizard so it can sit
   *  wherever the step's other errors do. */
  failed: React.ReactNode;
  saving: boolean;
  fields: Partial<Record<
    | "fname"
    | "lname"
    | "gender"
    | "birthday"
    | "phone"
    | "referralCode"
    | "countryTimezone"
    | "terms",
    React.ReactNode
  >>;
};

export type FreeTrialProfileFieldsHandle = {
  /**
   * Validate and save. Resolves true only once the profile is stored, so the
   * booking wizard can gate its "Next" on it.
   *
   * The wizard owns the submit button - these fields are part of its step 1,
   * not a form of their own - so validation has to be reachable from outside.
   */
  submitProfile: () => Promise<boolean>;
};

type ProfileFormData = {
  fname: string;
  lname: string;
  gender: string;
  birthday: Date | null;
  phone: string;
  /** Optional throughout - a referral is a bonus, never a blocker. */
  referralCode: string;
  /**
   * One control, two API values: the selected IANA zone. The country is
   * derived from it on submit - see countryForTimezone().
   */
  countryTimezone: string;
  terms: boolean;
};

/** The keys this form can render, and so the only ones setError() can reach. */
const FORM_FIELDS: (keyof ProfileFormData)[] = [
  "fname",
  "lname",
  "gender",
  "birthday",
  "phone",
  "referralCode",
  "countryTimezone",
  "terms",
];

function FreeTrialProfileFields(
  { missingFields, onCompleted, children }: Props,
  ref: React.Ref<FreeTrialProfileFieldsHandle>,
) {
  const t = useTranslations("auth.profileStep");
  const tRegister = useTranslations("register");
  const locale = useLocale();
  const pathname = usePathname();
  const {
    countries: countryOptions,
    timezones: timezoneOptions,
    loading: metadataLoading,
  } = useRegisterMetadata();

  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const phoneRef = useRef<IntlPhoneInputHandle>(null);
  const activeLocale = locale === "ar" ? "ar" : "en";

  // Nobody booking a trial was born this year; capping the picker keeps the
  // "at least 2 years old" rule from being discovered only at submit.
  const birthDatePickerMax = useMemo(
    () => new Date(new Date().getFullYear() - 1, 11, 31),
    [],
  );

  const needs = useMemo(() => new Set(missingFields), [missingFields]);

  const countryTimezoneGroups = useMemo(
    () => buildCountryTimezoneGroups(countryOptions, timezoneOptions),
    [countryOptions, timezoneOptions],
  );

  // Pre-select the browser's own zone when it is one we offer. Guessing right
  // most of the time beats leaving the field blank, and the user still sees
  // (and can change) what was chosen.
  const defaultCountryTimezone = useMemo(() => {
    // A US browser zone such as America/Detroit is folded into the named zone
    // the list offers for it (Eastern Time) - see canonicalTimezone().
    const detected = canonicalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? "");

    const offered = countryTimezoneGroups.some((group) =>
      group.zones.some((zone) => zone.value === detected),
    );

    return offered ? detected : "";
  }, [countryTimezoneGroups]);

  // Every rule below is copied from RegisterForm's own schema, message keys
  // included, so a profile completed here is held to exactly the same standard
  // as one typed into the register page - and the user sees the same wording
  // for the same mistake.
  //
  // Built from `needs` rather than declared whole: this form renders only the
  // fields Google could not supply, and validating a field that is not on
  // screen would block submission on an error nobody can see or fix.
  const schema = useMemo(() => {
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    const shape: Record<string, z.ZodTypeAny> = {};

    if (needs.has("fname")) {
      shape.fname = z
        .string()
        .trim()
        .min(1, tRegister("required_f_name"))
        .min(3, tRegister("invalid_f_name"))
        .max(100, tRegister("invalid_f_name"))
        .regex(ENGLISH_NAME_REGEX, tRegister("name_english_only"));
    }

    if (needs.has("lname")) {
      shape.lname = z
        .string()
        .trim()
        .min(1, tRegister("required_l_name"))
        .min(3, tRegister("invalid_l_name"))
        .max(100, tRegister("invalid_l_name"))
        .regex(ENGLISH_NAME_REGEX, tRegister("name_english_only"));
    }

    if (needs.has("gender")) {
      shape.gender = z.string().min(1, tRegister("required_gender"));
    }

    if (needs.has("birthday")) {
      shape.birthday = z
        .date({ message: tRegister("required_birth_date") })
        .nullable()
        .refine((val): val is Date => val !== null, {
          message: tRegister("required_birth_date"),
        })
        .refine((val) => val <= twoYearsAgo, {
          message: tRegister("birth_date_under_2"),
        });
    }

    // Country and timezone are asked for as a single "Country / Time Zone"
    // choice, so one rule covers both. Either being missing means the same
    // control is unanswered.
    if (needs.has("country") || needs.has("timezone")) {
      shape.countryTimezone = z.string().min(1, tRegister("required_timezone"));
    }

    if (needs.has("phone")) {
      // Mandatory. IntlPhoneInput's isValid() deliberately treats empty input
      // as valid ("required is the form schema's concern"), so without this
      // rule an empty phone sails through the format check below and only the
      // backend catches it.
      shape.phone = z.string().trim().min(1, tRegister("required_phone"));
    }

    // Always in the shape, never required: zod strips keys the schema does not
    // declare, and a stripped referralCode would make the submit handler read
    // `undefined.trim()` and fail the save for everyone.
    shape.referralCode = z.string().optional();

    if (needs.has("terms")) {
      shape.terms = z.boolean().refine((v) => v, tRegister("accept_terms"));
    }

    return z.object(shape);
  }, [needs, tRegister]);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ProfileFormData>({
    // The schema is assembled at runtime from `needs`, so its inferred type is
    // a partial record rather than ProfileFormData. The form's own field set is
    // fixed and the cast reconciles the two.
    resolver: zodResolver(schema) as unknown as Resolver<ProfileFormData>,
    defaultValues: {
      fname: "",
      lname: "",
      gender: "",
      birthday: null,
      phone: "",
      referralCode: "",
      // Best guess from the browser, which is right far more often than the
      // 'UTC' column default this is replacing. Still shown for confirmation,
      // and blank unless it is a zone we actually offer.
      countryTimezone: defaultCountryTimezone,
      terms: false,
    },
  });

  const onSubmit = async (data: ProfileFormData): Promise<boolean> => {
    // Country-aware format check, same guard RegisterForm applies. The backend
    // enforces E.164 anyway, but catching it here avoids a round trip and puts
    // the message on the field rather than in the banner.
    if (needs.has("phone") && !phoneRef.current?.isValid()) {
      setError("phone", { type: "manual", message: tRegister("invalid_phone") });

      return false;
    }

    setFailed(false);
    setSubmitting(true);

    try {
      // Send only what was actually asked for. The endpoint validates every
      // field as `sometimes|required`, so an unrelated key would be validated
      // (and could fail) for no reason.
      const payload: Record<string, unknown> = {};

      if (needs.has("fname")) payload.fname = data.fname;
      if (needs.has("lname")) payload.lname = data.lname;
      if (needs.has("gender")) payload.gender = data.gender;
      if (needs.has("birthday") && data.birthday) {
        payload.birthday = formatDateOnly(data.birthday);
      }
      if (needs.has("country") || needs.has("timezone")) {
        const timezone = data.countryTimezone;
        const country = countryForTimezone(timezone);

        if (needs.has("timezone")) payload.timezone = timezone;
        // Only zones with a country are offered, so this is always resolvable;
        // the guard keeps a malformed value from silently sending undefined.
        if (needs.has("country") && country) payload.country = country;
      }
      if (needs.has("phone")) payload.phone = data.phone.trim();

      // Always offered, never demanded - so it is sent only when filled.
      if (data.referralCode?.trim()) {
        payload.referral_code = data.referralCode.trim();
      }
      payload.terms = true;

      await completeGoogleProfileApi(payload);
      onCompleted();

      return true;
    } catch (error: unknown) {
      // Surface field errors on the fields themselves; anything else becomes
      // the generic banner.
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const fieldErrors =
          (error.response.data as { errors?: Record<string, string[]> })?.errors ?? {};

        // Only keys this form actually renders can be attached to a field; a
        // validation error on anything else (which would otherwise be set on a
        // field that does not exist, and so never shown) falls through to the
        // banner instead of failing silently.
        const attachable = Object.entries(fieldErrors).filter(([field]) =>
          FORM_FIELDS.includes(field as keyof ProfileFormData),
        );

        for (const [field, messages] of attachable) {
          setError(field as keyof ProfileFormData, {
            type: "server",
            message: messages?.[0] ?? "",
          });
        }

        if (attachable.length === Object.keys(fieldErrors).length && attachable.length > 0) {
          return false;
        }
      }

      setFailed(true);

      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // The wizard's "Next" drives this, so validation and save have to be
  // callable from the parent. handleSubmit resolves once the resolver has run:
  // on invalid input the onValid branch never fires and `saved` stays false,
  // which is exactly the signal the wizard needs to stay on step 1.
  useImperativeHandle(ref, () => ({
    submitProfile: async () => {
      let saved = false;

      await handleSubmit(async (data) => {
        saved = await onSubmit(data);
      })();

      return saved;
    },
  }));
  if (metadataLoading) {
    return children({ failed: null, saving: true, fields: {} });
  }

  // Each control is handed back on its own rather than as one rendered block.
  // The booking wizard interleaves them with its own questions - "who is
  // attending" sits beside Gender, Subject sits below them - which a single
  // section could not express. This component still owns all the form state
  // and validation; the wizard only decides where each control goes.
  return children({
    failed: failed ? (
      <div className="rounded-md border-l-4 border-danger bg-danger/10 p-3 text-sm text-danger">
        {t("failed")}
      </div>
    ) : null,
    saving: submitting,
    fields: {
      fname: needs.has("fname") ? (
        <FormInput
          label={tRegister("first_name")}
          placeholder={tRegister("enter_first_name")}
          error={errors.fname?.message}
          register={withEnglishNameFilter(register("fname"))}
        />
      ) : null,

      lname: needs.has("lname") ? (
        <FormInput
          label={tRegister("last_name")}
          placeholder={tRegister("enter_last_name")}
          error={errors.lname?.message}
          register={withEnglishNameFilter(register("lname"))}
        />
      ) : null,

      gender: needs.has("gender") ? (
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <FormSelect
              label={tRegister("gender")}
              placeholder={tRegister("select_gender")}
              error={errors.gender?.message}
              options={[
                { value: "male", label: tRegister("male") },
                { value: "female", label: tRegister("female") },
              ]}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      ) : null,

      birthday: needs.has("birthday") ? (
        <>
          {/* DateInput renders only the control - the label and error are the
              parent's job, same as in RegisterForm. */}
          <label className="mb-1.5 block text-sm text-primary">
            {tRegister("birth_date")}
          </label>
          <Controller
            name="birthday"
            control={control}
            render={({ field }) => (
              <DateInput
                onChange={field.onChange}
                locale={activeLocale}
                maxDate={birthDatePickerMax}
                placeholder={tRegister("enter_birth_date")}
                value={field.value ?? undefined}
              />
            )}
          />
          {errors.birthday?.message ? (
            <p className="mt-1 text-xs text-danger">{errors.birthday.message}</p>
          ) : null}
        </>
      ) : null,

      phone: needs.has("phone") ? (
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <IntlPhoneInput
              ref={phoneRef}
              label={tRegister("phone")}
              error={errors.phone?.message}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      ) : null,

      referralCode: (
        <FormInput
          label={tRegister("referral_code_optional")}
          placeholder={tRegister("enter_referral_code")}
          error={errors.referralCode?.message}
          register={register("referralCode")}
        />
      ),

      countryTimezone:
        needs.has("country") || needs.has("timezone") ? (
          <Controller
            name="countryTimezone"
            control={control}
            render={({ field }) => (
              <CountryTimezoneSelect
                label={t("country_timezone")}
                placeholder={t("select_country_timezone")}
                error={errors.countryTimezone?.message}
                groups={countryTimezoneGroups}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        ) : null,

      terms: needs.has("terms") ? (
        <div className="flex items-center gap-2">
          {/* `register.terms` is a RICH message carrying <terms>…</terms>, so
              it has to be read with t.rich - calling it as a plain string
              renders the raw tag instead of a working link. Same markup and
              destination as RegisterForm, so both point at the one terms page
              for the current region. */}
          <FormCheckbox
            label={tRegister.rich("terms", {
              terms: (chunks) => (
                <Link
                  href={getRegionLocaleHref(pathname, locale, "/terms-of-service")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="underline underline-offset-2 hover:opacity-80"
                >
                  {chunks}
                </Link>
              ),
            })}
            error={errors.terms?.message}
            register={register("terms")}
          />
        </div>
      ) : null,
    },
  });
}

export default forwardRef(FreeTrialProfileFields);
